(() => {
  if (customElements.get('store-availabilities')) return;

  class StoreAvailabilities extends HTMLElement {
    connectedCallback() {
      this.productSection = this.closest('[data-section-type="product"]');
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('[data-store-availability-open]');
      if (!this.productSection || typeof this.dialog.showModal !== 'function') return;

      this.content = this.querySelector('[data-store-availability-content]');
      this.indicator = this.querySelector('[data-store-availability-indicator]');
      this.loading = this.querySelector('[data-store-availability-loading]');
      this.error = this.querySelector('[data-store-availability-error]');
      this.selectionMessage = this.querySelector('[data-store-availability-selection]');
      this.onlineOnlyMessage = this.querySelector('[data-store-availability-online-only]');
      this.variantId = this.content.querySelector('[data-variant-id]').dataset.variantId;
      this.sortLocations();
      this.updateAvailability();

      this.onClick = this.handleClick.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);
      this.onVariantChange = (event) => this.selectVariant(event.detail.variant);
      this.onClose = () => {
        if (!document.querySelector('.store-availabilities__dialog[open]')) {
          document.documentElement.classList.remove('store-availabilities-open');
        }
      };
      this.addEventListener('click', this.onClick);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('keydown', this.onKeyDown);
      this.productSection.addEventListener('variant:change', this.onVariantChange);
    }

    disconnectedCallback() {
      this.request?.abort();
      this.removeEventListener('click', this.onClick);
      this.productSection?.removeEventListener('variant:change', this.onVariantChange);
      this.dialog?.removeEventListener('close', this.onClose);
      this.dialog?.removeEventListener('keydown', this.onKeyDown);
      if (this.dialog?.open) this.dialog.close();
      this.onClose?.();
    }

    handleClick(event) {
      if (event.target.closest('[data-store-availability-open]')) {
        this.dialog.showModal();
        document.documentElement.classList.add('store-availabilities-open');
      } else if (event.target.closest('[data-store-availability-close]')) {
        this.dialog.close();
      } else if (event.target.closest('[data-store-availability-retry]')) {
        this.loadContent();
      } else if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
          this.dialog.close();
        }
      }
    }

    handleKeyDown(event) {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll('button:not(:disabled), a[href], [tabindex="0"]')).filter((element) => element.getClientRects().length);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    selectVariant(variant) {
      const variantId = variant ? String(variant.id) : '';
      if (variantId === this.variantId) return;

      this.request?.abort();
      this.variantId = variantId;
      this.trigger.disabled = !variantId;
      this.trigger.hidden = false;
      this.onlineOnlyMessage.hidden = true;
      this.selectionMessage.hidden = Boolean(variantId);
      if (!variantId) {
        this.dialog.close();
        this.indicator.removeAttribute('data-status');
        return;
      }
      this.loadContent();
    }

    sortLocations(content = this.content) {
      if (this.dataset.showAvailableFirst !== 'true') return;
      const tableBody = content.querySelector('tbody');
      if (!tableBody) return;

      const rows = Array.from(tableBody.rows);
      rows.sort((first, second) => Number(second.dataset.storeLocationAvailable === 'true') - Number(first.dataset.storeLocationAvailable === 'true'));
      tableBody.append(...rows);
    }

    updateAvailability() {
      const result = this.content.querySelector('[data-store-availability-result]');
      this.indicator.dataset.status = result.dataset.hasAvailability === 'true' ? 'available' : 'unavailable';
      const onlineOnly = result.dataset.variantAvailable === 'true' && result.dataset.hasAvailability !== 'true';
      this.trigger.hidden = onlineOnly;
      this.onlineOnlyMessage.hidden = !onlineOnly;
      if (onlineOnly) this.dialog.close();
    }

    async loadContent() {
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const { variantId } = this;
      if (this.dialog.open && (this.content.contains(document.activeElement) || this.error.contains(document.activeElement))) {
        this.querySelector('[data-store-availability-close]').focus();
      }
      this.loading.hidden = false;
      this.error.hidden = true;
      this.content.hidden = true;
      this.content.setAttribute('aria-busy', 'true');
      this.indicator.removeAttribute('data-status');

      // Retain locale and theme-preview parameters when refreshing the selected variant.
      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.search = window.location.search;
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', 'store-availabilities');
      url.searchParams.set('variant', variantId);

      try {
        const response = await fetch(url.toString(), { signal: request.signal });
        if (!response.ok) throw new Error('Unable to load store availability');
        const html = await response.text();
        const result = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-store-availability-result]');
        if (!result || result.dataset.variantId !== variantId) throw new Error('Invalid store availability response');
        if (request.signal.aborted || this.variantId !== variantId) return;

        this.sortLocations(result);
        this.content.replaceChildren(result);
        this.content.hidden = false;
        this.updateAvailability();
      } catch (error) {
        if (request.signal.aborted || this.variantId !== variantId) return;
        this.error.hidden = false;
      } finally {
        if (!request.signal.aborted && this.variantId === variantId) {
          this.loading.hidden = true;
          this.content.removeAttribute('aria-busy');
        }
      }
    }
  }

  customElements.define('store-availabilities', StoreAvailabilities);
})();
